<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Statistics Cards -->
    <div class="statistics_cards_wrapper">
      <div class="row justify-content-center align-items-center w-100">
        <!-- Start:: Total Applications Card -->
        <div class="col-md-6">
          <div class="card_wrapper">
            <div class="card_data">
              <h3>{{ statistics.total || 0 }}</h3>
              <h6>{{ $t("PLACEHOLDERS.totalApplications") }}</h6>
            </div>
            <div class="card_icon">
              <i class="fal fa-file-alt"></i>
            </div>
          </div>
        </div>
        <!-- End:: Total Applications Card -->

        <!-- Start:: New Applications Card -->
        <div class="col-md-6">
          <div class="card_wrapper">
            <div class="card_data">
              <h3>{{ statistics.new_applications || 0 }}</h3>
              <h6>{{ $t("PLACEHOLDERS.newApplications") }}</h6>
            </div>
            <div class="card_icon">
              <i class="fal fa-bell"></i>
            </div>
          </div>
        </div>
        <!-- End:: New Applications Card -->
      </div>
    </div>
    <!-- End:: Statistics Cards -->

    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Applicant Name Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.applicantName')"
                v-model.trim="filterOptions.userName"
              />
              <!-- End:: Applicant Name Input -->

              <!-- Start:: Phone Input -->
              <base-input
                col="6"
                type="tel"
                :placeholder="$t('PLACEHOLDERS.phone')"
                v-model.trim="filterOptions.userMobile"
              />
              <!-- End:: Phone Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.manage_all_job_applications") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ noDataMessage }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <!-- Start:: Applicant Name -->
        <template v-slot:[`item.client_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.client || !item.client.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.client.name }}</h6>
        </template>
        <!-- End:: Applicant Name -->

        <!-- Start:: Job Name -->
        <template v-slot:[`item.job_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.job || !item.job.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.job.name }}</h6>
        </template>
        <!-- End:: Job Name -->

        <!-- Start:: Phone -->
        <template v-slot:[`item.mobile`]="{ item }">
          <h6 class="text-danger" v-if="!item.client || !item.client.mobile">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.client.mobile }}</h6>
        </template>
        <!-- End:: Phone -->

        <!-- Start:: Application Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <h6 class="text-danger" v-if="!item.created_at">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.created_at }}</h6>
        </template>
        <!-- End:: Application Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status_enum)"
            text-color="white"
            small
          >
            {{ item.status }}
          </v-chip>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('job applications show', 'job_applications')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="
                item.status_enum === 'pending' &&
                $can('job applications changestatus', 'job_applications')
              "
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.accept") }}</span>
              </template>
              <button class="" @click="openAcceptModal(item)">
                <i class="fal fa-check"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="
                item.status_enum === 'pending' &&
                $can('job applications changestatus', 'job_applications')
              "
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.reject") }}</span>
              </template>
              <button class="" @click="openRejectModal(item)">
                <i class="fal fa-times"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Accept Modal -->
    <v-dialog v-model="acceptModalIsOpen" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.acceptApplicationConfirm") }}
        </v-card-title>

        <v-card-text>
          <form @submit.prevent="handleAccept">
            <div class="row">
              <!-- Start:: Contract File Upload -->
              <base-name-preview-file-upload-input
                col="12"
                identifier="contract_file"
                :placeholder="$t('PLACEHOLDERS.uploadContract')"
                accept=".pdf"
                @selectFile="selectContractFile"
                required
              />
              <!-- End:: Contract File Upload -->

              <!-- Start:: Salary Input -->
              <base-input
                col="12"
                type="number"
                :placeholder="$t('PLACEHOLDERS.salary')"
                v-model.trim="acceptFormData.salary"
              />
              <!-- End:: Salary Input -->
            </div>
          </form>
        </v-card-text>

        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            @click="handleAccept"
            :disabled="!acceptFormData.contract_file || isWaitingRequest"
          >
            {{ $t("BUTTONS.save") }}
          </v-btn>

          <v-btn
            class="modal_cancel_btn"
            @click="acceptModalIsOpen = false"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Accept Modal -->

    <!-- Start:: Reject Modal -->
    <v-dialog v-model="rejectModalIsOpen" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.rejectApplicationConfirm") }}
        </v-card-title>

        <v-card-text>
          <form @submit.prevent="handleReject">
            <div class="row">
              <!-- Start:: Rejection Reason Input -->
              <base-input
                col="12"
                type="textarea"
                rows="4"
                :placeholder="$t('PLACEHOLDERS.rejectionReason')"
                v-model.trim="rejectFormData.rejection_reason"
                required
              />
              <!-- End:: Rejection Reason Input -->
            </div>
          </form>
        </v-card-text>

        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            @click="handleReject"
            :disabled="
              !rejectFormData.rejection_reason ||
              rejectFormData.rejection_reason.length < 10 ||
              isWaitingRequest
            "
          >
            {{ $t("BUTTONS.send") }}
          </v-btn>

          <v-btn
            class="modal_cancel_btn"
            @click="rejectModalIsOpen = false"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Reject Modal -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BaseNamePreviewFileUploadInput from "@/components/formInputs/BaseNamePreviewFileUploadInput.vue";

export default {
  name: "AllJobApplications",
  components: {
    BaseNamePreviewFileUploadInput,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Statistics Data
      statistics: {
        total: 0,
        new_applications: 0,
      },
      // End:: Statistics Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        userName: null,
        userMobile: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      noDataMessage: this.$t("TABLES.noData"),
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.applicantName"),
          value: "client_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.jobName"),
          value: "job_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.phone"),
          value: "mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.applicationDate"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Accept Modal Data
      acceptModalIsOpen: false,
      selectedItem: null,
      acceptFormData: {
        contract_file: null,
        salary: null,
      },
      // End:: Accept Modal Data

      // Start:: Reject Modal Data
      rejectModalIsOpen: false,
      rejectFormData: {
        rejection_reason: null,
      },
      // End:: Reject Modal Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(statusEnum) {
      switch (statusEnum) {
        case "pending":
          return "orange";
        case "accepted":
          return "green";
        case "rejected":
          return "red";
        default:
          return "grey";
      }
    },
    // End:: Get Status Color

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/job-applications/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.userName = null;
      this.filterOptions.userMobile = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/job-applications/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "job-applications",
          params: {
            page: this.paginations.current_page,
            userName: this.filterOptions.userName,
            userMobile: this.filterOptions.userMobile,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
        this.statistics.total = res.data.data.meta.total;
        this.statistics.new_applications =
          res.data.number_of_new_applications || 0;

        if (
          this.tableRows.length === 0 &&
          (this.filterOptions.userName || this.filterOptions.userMobile)
        ) {
          this.noDataMessage = this.$t("MESSAGES.noSearchResults");
        } else {
          this.noDataMessage = this.$t("TABLES.noData");
        }
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(item) {
      this.$router.push({ path: `/job-applications/show/${item.id}` });
    },
    // ===== End:: Show

    // Start:: Open Accept Modal
    openAcceptModal(item) {
      this.selectedItem = item;
      this.acceptModalIsOpen = true;
    },
    // End:: Open Accept Modal

    // Start:: Open Reject Modal
    openRejectModal(item) {
      this.selectedItem = item;
      this.rejectModalIsOpen = true;
    },
    // End:: Open Reject Modal

    // Start:: Select Contract File
    selectContractFile(selectedFile) {
      this.acceptFormData.contract_file = selectedFile.file;
    },
    // End:: Select Contract File

    // Start:: Handle Accept
    async handleAccept() {
      if (!this.acceptFormData.contract_file) {
        this.$message.error(this.$t("MESSAGES.contractFileRequired"));
        return;
      }

      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();

      REQUEST_DATA.append("status", "accepted");
      REQUEST_DATA.append("contract_file", this.acceptFormData.contract_file);

      if (this.acceptFormData.salary) {
        REQUEST_DATA.append("salary", this.acceptFormData.salary);
      }

      try {
        await this.$axios({
          method: "POST",
          url: `job-applications/change-status/${this.selectedItem.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.acceptModalIsOpen = false;
        this.acceptFormData.contract_file = null;
        this.acceptFormData.salary = null;
        this.$message.success(this.$t("MESSAGES.applicationAccepted"));
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Handle Accept

    // Start:: Handle Reject
    async handleReject() {
      if (
        !this.rejectFormData.rejection_reason ||
        this.rejectFormData.rejection_reason.length < 10
      ) {
        this.$message.error(this.$t("MESSAGES.rejectionReasonRequired"));
        return;
      }

      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();

      REQUEST_DATA.append("status", "rejected");
      REQUEST_DATA.append(
        "rejection_reason",
        this.rejectFormData.rejection_reason
      );

      try {
        await this.$axios({
          method: "POST",
          url: `job-applications/change-status/${this.selectedItem.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.rejectModalIsOpen = false;
        this.rejectFormData.rejection_reason = null;
        this.$message.success(this.$t("MESSAGES.applicationRejected"));
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Handle Reject
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.statistics_cards_wrapper {
  margin-bottom: 30px;
}

.card_wrapper {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 25px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  margin-bottom: 15px;
}

.card_wrapper:hover {
  transform: translateY(-5px);
}

.card_data h3 {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 5px;
}

.card_data h6 {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

.card_icon {
  font-size: 50px;
  opacity: 0.3;
}

span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.btn_success {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

.btn_success:hover {
  background-color: #218838 !important;
  border-color: #1e7e34 !important;
}

.btn_danger {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
}

.btn_danger:hover {
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
}
</style>
